import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { LazyDuckDBProvider } from './context/LazyDuckDBContext';
import { AnimatePresence } from 'framer-motion';
import PageTransition from './components/ui/PageTransition';
import DashboardView from './components/DashboardView';
import KommunDashboardView from './components/KommunDashboardView';
import AboutView from './components/AboutView';
import Header from './components/Header';
import BottomNav from './components/BottomNav';
import { DashboardLoadingSkeleton } from './components/feedback/Skeleton';
import { FancyLoader } from './components/feedback/FancyLoader';
// import LoadingSpinner from './components/LoadingSpinner'; // Not used in current implementation
import DesktopLandingPage from './components/DesktopLandingPage';
import ErrorBoundary from './components/ErrorBoundary';
import { useDuckDB } from './context/LazyDuckDBContext';
import { usePrefetchDashboard } from './hooks/usePrefetchDashboard';
import { observeWebVitals, logPerformanceMetrics } from './utils/performance';
import { enableOverflowDetection } from './utils/overflowDetector';
import { warmDuckdbAssetsIdle } from './utils/warmDuckdbAssets';
import { isEnginePath } from './constants/engineRoutes';
import './App.css';

/** Runs prefetch only when LazyDuckDBProvider is mounted (dashboard routes). */
const DashboardPrefetchHost: React.FC = () => {
  usePrefetchDashboard();
  return null;
};

/**
 * DuckDB WASM loads only on engine routes; landing/about skip it for FCP/memory.
 * Header, routed views, and BottomNav are all rendered as children here so they share
 * one LazyDuckDBProvider instance on /kommun, /skola, /dashboard (not only the route outlet).
 */
const ConditionalDuckDBShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const needsDuckDB = isEnginePath(location.pathname);
  if (!needsDuckDB) {
    return <>{children}</>;
  }
  return (
    <LazyDuckDBProvider delayMs={0}>
      <DashboardPrefetchHost />
      {children}
    </LazyDuckDBProvider>
  );
};

// Wrapper component for dashboard pages that require DuckDB
const DashboardWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { ready, error, isInitializing } = useDuckDB();

  // Show skeleton while DuckDB is initializing (mobile-first perceived performance)
  if (isInitializing || (!ready && !error)) {
    return (
      <div className="relative min-h-screen bg-slate-50 dark:bg-slate-950">
        <DashboardLoadingSkeleton />
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-50/20 dark:bg-slate-950/20 backdrop-blur-md">
          <div className="p-8 rounded-3xl bg-white/40 dark:bg-slate-900/40 border border-white/20 dark:border-white/10 shadow-2xl flex flex-col items-center gap-6 max-w-sm text-center">
            <div className="text-emerald-500 dark:text-emerald-400">
              <FancyLoader label={null} sizePx={80} />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">Startar eduInsights</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                Vi förbereder analysmotorn och laddar in den senaste statistiken...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error message if initialization fails
  if (error) {
    return (
      <div className="w-screen h-screen flex items-center justify-center p-8 bg-gray-50 dark:bg-slate-900">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative max-w-md" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      </div>
    );
  }

  // Show the dashboard content when ready
  return (
    <div className="pb-20 sm:pb-0 pt-16 sm:pt-20">
      {children}
    </div>
  );
};

// Test deployment - CI/CD setup verification

/** Shared chrome + animated routes; must stay a direct descendant of ConditionalDuckDBShell. */
const AppChromeAndRoutes: React.FC = () => (
  <>
    <Header />
    <AnimatePresence mode="wait">
      <AnimatedRoutesContent />
    </AnimatePresence>
    <BottomNav />
  </>
);

const AnimatedRoutesContent = () => {
  const location = useLocation();

  return (
    <Routes location={location} key={location.pathname}>
      <Route path="/" element={
        <PageTransition>
          <DesktopLandingPage />
        </PageTransition>
      } />
      <Route path="/kommun" element={
        <PageTransition>
          <DashboardWrapper>
            <KommunDashboardView />
          </DashboardWrapper>
        </PageTransition>
      } />
      <Route path="/skola" element={
        <PageTransition>
          <DashboardWrapper>
            <DashboardView />
          </DashboardWrapper>
        </PageTransition>
      } />
      <Route path="/dashboard" element={
        <PageTransition>
          <DashboardWrapper>
            <DashboardView />
          </DashboardWrapper>
        </PageTransition>
      } />
      <Route path="/about" element={
        <PageTransition>
          <AboutView />
        </PageTransition>
      } />
    </Routes>
  );
};

function App() {
  useEffect(() => {
    warmDuckdbAssetsIdle();
  }, []);

  // Initialize performance monitoring and viewport height
  useEffect(() => {
    // Set --vh variable for mobile browsers
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVh();
    window.addEventListener('resize', setVh);

    // Start observing Web Vitals
    observeWebVitals();

    // Enable overflow detection in development
    if (process.env.NODE_ENV === 'development') {
      enableOverflowDetection();
    }

    // Add global error handlers
    const handleError = (event: ErrorEvent) => {
      console.error('Global error caught:', event.error);
      console.error('Error details:', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Log performance metrics after load
    const handleLoad = () => {
      setTimeout(() => {
        logPerformanceMetrics();
      }, 1000);
    };

    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
    }

    // Always return cleanup function
    return () => {
      window.removeEventListener('load', handleLoad);
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('resize', setVh);
    };
  }, []);

  // For all users, show landing page first and load DuckDB in background
  // Only show loading/error states when user navigates to dashboard pages

  return (
    <ErrorBoundary>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <div className="App min-h-screen bg-gray-50 text-slate-900 dark:bg-slate-900 dark:text-slate-100 clip-x touch-pan-y">
          <ConditionalDuckDBShell>
            <AppChromeAndRoutes />
          </ConditionalDuckDBShell>
        </div>
      </Router>
    </ErrorBoundary>
  );
}

export default App;